import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Download, FileText, Video } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { safeUUID } from "@/lib/utils";

interface SampleResource {
  id: string;
  title: string;
  type: string;
  category: string;
  stage: string;
  description: string;
  downloads?: number;
  videoUrl?: string;
  videoTitle?: string;
  fileUrl?: string;
}

// Display label for the type field so raw values never render.
const TYPE_LABEL: Record<string, string> = {
  Sample: "Sample document",
  Document: "Document",
  Video: "Video",
};

export default function TemplateDetail() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [resource, setResource] = useState<SampleResource | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (templateId && templateId !== "1" && templateId !== "2" && templateId !== "3" && templateId !== "4" && !safeUUID(templateId)) {
      console.error(`Invalid sample id: "${templateId}"`);
      navigate('/resources');
      return;
    }

    // Sample data used while the sample library is being wired to the database.
    const samples: Record<string, SampleResource> = {
      "1": {
        id: "1",
        title: "Sample Constitution for Family Associations",
        type: "Document",
        category: "Constitutional",
        stage: "Pre-Setup",
        description: "A ready to adapt sample you can use to draft your own Constitution. It covers every section you will need to set up your family association. Use it as a starting point and shape it to fit your family.",
        downloads: 234,
        videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
        videoTitle: "Understanding Your Constitution",
        fileUrl: "/templates/constitutional-template.pdf",
      },
      "3": {
        id: "3",
        title: "Sample Asset Transfer Agreement",
        type: "Sample",
        category: "Asset Transfer",
        stage: "Setup",
        description: "A sample agreement you can use to move assets into your Association. It gives you a clear frame for recording each transfer. It includes sections for real estate, financial accounts, and personal property.",
        downloads: 198,
        videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
        videoTitle: "How to Use the Sample Asset Transfer Agreement",
        fileUrl: "/templates/asset-transfer-template.pdf",
      },
      "4": {
        id: "4",
        title: "Banking Setup Guide",
        type: "Document",
        category: "Banking",
        stage: "Setup",
        description: "A step by step guide to setting up banking for your Association. It walks you through choosing a bank, opening accounts, naming who can sign, and putting basic money controls in place.",
        downloads: 145,
        fileUrl: "/templates/banking-setup-guide.pdf",
      },
      "6": {
        id: "6",
        title: "Sample Board Meeting Minutes",
        type: "Sample",
        category: "Governance",
        stage: "Operating",
        description: "A sample layout for writing your board meeting minutes (the written record of a meeting). It has spaces for who attended, what was on the agenda, what was decided, and what happens next.",
        downloads: 87,
        videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
        videoTitle: "Best Practices for Board Meeting Records",
        fileUrl: "/templates/meeting-minutes-template.pdf",
      },
    };

    const found = samples[templateId || ""];
    if (found) {
      setResource(found);
    }
    setLoading(false);
  }, [templateId]);

  const handleDownload = () => {
    if (resource?.fileUrl) {
      toast({
        title: "Download started",
        description: `Downloading ${resource.title}...`,
      });
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </MainLayout>
    );
  }

  if (!resource) {
    return (
      <MainLayout>
        <div className="space-y-6">
          <Button variant="ghost" onClick={() => navigate("/resources")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Resources
          </Button>
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <FileText className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Sample not found</p>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  const typeLabel = TYPE_LABEL[resource.type] ?? resource.type;

  return (
    <MainLayout>
      <div className="space-y-6 max-w-5xl mx-auto">
        <Button variant="ghost" onClick={() => navigate("/resources")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Resources
        </Button>

        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="space-y-4 flex-1">
                <div className="flex items-center gap-2">
                  <FileText className="h-6 w-6 text-primary" />
                  <Badge variant="secondary">{typeLabel}</Badge>
                  <Badge variant="outline">{resource.category}</Badge>
                  <Badge variant="outline">{resource.stage}</Badge>
                </div>
                <CardTitle className="text-3xl">{resource.title}</CardTitle>
                <CardDescription className="text-base">{resource.description}</CardDescription>
              </div>
              <Button onClick={handleDownload} size="lg" className="ml-4">
                <Download className="mr-2 h-5 w-5" />
                Download
              </Button>
            </div>
          </CardHeader>

          {resource.videoUrl && (
            <>
              <Separator />
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Video className="h-5 w-5 text-primary" />
                    <h3 className="text-xl font-semibold">
                      {resource.videoTitle || "Walkthrough video"}
                    </h3>
                  </div>
                  <div className="aspect-video rounded-lg overflow-hidden bg-muted">
                    <iframe
                      src={resource.videoUrl}
                      title={resource.videoTitle || "Sample walkthrough video"}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                </div>
              </CardContent>
            </>
          )}

          <Separator />
          <CardContent className="pt-6">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-3">About this sample</h3>
                <div className="prose max-w-none">
                  <p className="text-muted-foreground leading-relaxed">
                    This sample is here to help you {resource.stage === "Pre-Setup" ? "prepare for" : resource.stage === "Setup" ? "set up" : "run"} your family association.
                    It shows one clear way to do it, with the sections and wording most families need.
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-3">How to use it</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-foreground">1.</span>
                    <span>Download the sample using the button above.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-foreground">2.</span>
                    <span>Read every section and change the wording so it fits your family.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-foreground">3.</span>
                    <span>Ask your legal advisor to look it over before you sign.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-foreground">4.</span>
                    <span>Save a copy in your Association records so you can find it later.</span>
                  </li>
                </ul>
              </div>

              {resource.downloads && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground pt-4 border-t">
                  <Download className="h-4 w-4" />
                  <span>{resource.downloads} downloads</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
}
